import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, Users, Key, Pencil } from 'lucide-react';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ user_id: '', name: '', email: '', password: '', role: 'operations', location_id: 'main-kitchen' });
  const [passwordForm, setPasswordForm] = useState({ userId: '', newPassword: '' });
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);

  useEffect(() => { fetchUsers(); fetchLocations(); }, []);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data);
    } catch (e) {
      // silent
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (e) {
      // Not admin or not logged in
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/users`, form);
      toast.success('User created');
      setShowAdd(false);
      setForm({ user_id: '', name: '', email: '', password: '', role: 'operations', location_id: 'main-kitchen' });
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdate = async () => {
    if (!editUser) return;
    try {
      const updateData = { name: form.name, role: form.role, location_id: form.location_id };
      if (form.password) updateData.password = form.password;
      await axios.put(`${API}/users/${editUser._id}`, updateData);
      toast.success('User updated');
      setEditUser(null);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user "${user.name || user.user_id}"?`)) return;
    try {
      await axios.delete(`${API}/users/${user._id}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleChangePassword = async () => {
    try {
      await axios.put(`${API}/users/${passwordForm.userId}`, { password: passwordForm.newPassword });
      toast.success('Password changed');
      setShowPasswordDialog(false);
      setPasswordForm({ userId: '', newPassword: '' });
    } catch (e) {
      toast.error('Failed to change password');
    }
  };

  return (
    <div data-testid="user-management">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-[var(--color-primary)]" />
          <h3 className="text-base font-semibold">User Management</h3>
        </div>
        <Button size="sm" onClick={() => { setShowAdd(true); setEditUser(null); setForm({ user_id: '', name: '', email: '', password: '', role: 'operations', location_id: 'main-kitchen' }); }} className="btn-primary" data-testid="add-user-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add User
        </Button>
      </div>

      {/* User list */}
      <div className="space-y-2 mb-6">
        {users.map(u => (
          <div key={u._id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg" data-testid={`user-${u._id}`}>
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                u.role === 'admin' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
              }`}>
                {(u.name || u.user_id || '?')[0].toUpperCase()}
              </div>
              <div>
                <div className="font-medium text-sm">{u.name || u.user_id}</div>
                <div className="text-xs text-gray-400">ID: {u.user_id} | {u.email}</div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">
                  📍 {locations.find(l => l.id === (u.location_id || 'main-kitchen'))?.name || u.location_id || 'Main Kitchen'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded ${
                u.role === 'admin' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
              }`}>{u.role}</span>
              <button onClick={() => {
                setEditUser(u);
                setShowAdd(false);
                setForm({ user_id: u.user_id, name: u.name, email: u.email, password: '', role: u.role, location_id: u.location_id || 'main-kitchen' });
              }} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded">
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => {
                setPasswordForm({ userId: u._id, newPassword: '' });
                setShowPasswordDialog(true);
              }} className="p-1.5 text-amber-500 hover:text-amber-600 hover:bg-amber-50 rounded" title="Change password">
                <Key className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => handleDelete(u)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add / Edit User Form */}
      {(showAdd || editUser) && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg mb-4">
          <h4 className="text-sm font-semibold mb-3">{editUser ? 'Edit User' : 'New User'}</h4>
          <div className="grid grid-cols-2 gap-3">
            {!editUser && (
              <Input value={form.user_id} onChange={(e) => setForm(f => ({...f, user_id: e.target.value}))} placeholder="User ID (login)" data-testid="user-form-id" />
            )}
            <Input value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} placeholder="Full Name" data-testid="user-form-name" />
            {!editUser && (
              <Input value={form.email} onChange={(e) => setForm(f => ({...f, email: e.target.value}))} placeholder="Email (optional)" />
            )}
            <Input type="password" value={form.password} onChange={(e) => setForm(f => ({...f, password: e.target.value}))} placeholder={editUser ? "New password (leave blank to keep)" : "Password"} data-testid="user-form-password" />
            <Select value={form.role} onValueChange={(v) => setForm(f => ({...f, role: v}))}>
              <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="operations">Operations</SelectItem>
              </SelectContent>
            </Select>
            <Select value={form.location_id} onValueChange={(v) => setForm(f => ({...f, location_id: v}))}>
              <SelectTrigger data-testid="user-form-location"><SelectValue placeholder="Location" /></SelectTrigger>
              <SelectContent>
                {locations.filter(l => l.active).map(l => (
                  <SelectItem key={l.id} value={l.id}>📍 {l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={editUser ? handleUpdate : handleCreate} className="btn-primary" data-testid="user-form-submit">
              {editUser ? 'Update' : 'Create'} User
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowAdd(false); setEditUser(null); }}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change Password</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <Input type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm(f => ({...f, newPassword: e.target.value}))} placeholder="New password" data-testid="change-password-input" />
            <Button onClick={handleChangePassword} disabled={!passwordForm.newPassword} className="btn-primary w-full" data-testid="change-password-submit">Change Password</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
